import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockSimple, SparkleIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useCaseFrameLeaveGuard } from "@/zapi-frontend/stores/caseFrameLeaveGuard";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { selectIsChapterGenerating, useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import { ClickAwayListener } from "@libs/zapi-ui/custom";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { SidebarButton, SidebarGroupLabel, SidebarSubMenu } from "./SidebarButton";
import {
  SidebarHomeIcon,
  SidebarMemoIcon,
  SidebarFrameIcon,
  SidebarVaultIcon,
  SidebarExportIcon,
  SidebarExecSummaryIcon
} from "@libs/zapi-ui/assets";
import { SidebarCollapseFooter } from "@/zapi-frontend/shared/ui/SidebarCollapseFooter/SidebarCollapseFooter";
import { SyncedSpinner } from "@/zapi-frontend/shared/ui/SyncedSpinner";
import {
  DEEP_DIVE_SECTIONS,
  EXEC_SUMMARY_SLUG,
  FULL_SUMMARY_SLUG,
  isAppendixChapter
} from "@/zapi-frontend/shared/lib/projectChapters";

export { DEEP_DIVE_SECTIONS, EXEC_SUMMARY_SLUG, FULL_SUMMARY_SLUG, isAppendixChapter };

export type TCreationView = "projectSetup" | "caseFrame";

type TProjectSidebarProps = {
  creation?: {
    view: TCreationView;
    onViewChange: (view: TCreationView) => void;
    caseFrameLocked: boolean;
  };
};

function LockBadge() {
  return <LockSimple className="size-3 shrink-0 text-innoveraZinc-400" weight="regular" />;
}

// Only chat-driven generation gets a spinner. During project setup every chapter is
// generating at once and the chat progress list already reports each of them (INV-2334).
function ChapterIndicator({ chapter, projectId }: { chapter: TChapter; projectId: string }) {
  const isSetupActive = useProjectSetupStore((s) => !!s.activeSetupTasks[projectId]);
  const isGenerating = useChatStore(selectIsChapterGenerating(projectId, String(chapter.id)));

  if (isSetupActive || !isGenerating) return null;
  return (
    <SyncedSpinner className="size-3 shrink-0 rounded-full border-2 border-innoveraZinc-200 border-t-innoveraZinc-500" />
  );
}

export function ProjectSidebar({ creation }: TProjectSidebarProps) {
  const [, setSearchParams] = useSearchParams();
  const { id: projectId } = useParams() as { id: string };
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation("projectHome");

  const { selectedProject } = useProjectStore();
  const {
    projectChapters,
    activeChapter,
    setActiveChapter,
    isOpenChapterSidebar,
    toggleChapterSidebar,
    viewMode,
    setViewMode
  } = useChapterStore();
  const { isLabs } = useMeStore();
  const { closeHandler } = useProjectSidebarStore();
  const { isDirty: isCaseFrameDirty, setPendingLeave: setCaseFramePendingLeave } = useCaseFrameLeaveGuard();

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(creation ? ["frame"] : []));

  const collapsed = !isOpenChapterSidebar;

  const chapters = useMemo(() => {
    const execChapter =
      projectChapters.find((c) => c.projectTemplateChapter.slug === EXEC_SUMMARY_SLUG) ??
      projectChapters.find(
        (c) => c.projectTemplateChapter.isExecutiveSummary && c.projectTemplateChapter.slug !== FULL_SUMMARY_SLUG
      );
    const memoChapter = projectChapters.find((c) => c.projectTemplateChapter.slug === FULL_SUMMARY_SLUG);
    const specialIds = new Set([execChapter?.id, memoChapter?.id]);

    const appendices = projectChapters.filter((c) => !specialIds.has(c.id) && !c.isHidden && isAppendixChapter(c));
    const regular = projectChapters.filter((c) => !specialIds.has(c.id) && !c.isHidden && !isAppendixChapter(c));

    const templateHasSections = regular.some((c) => c.projectTemplateChapter.section);
    const sections = DEEP_DIVE_SECTIONS.map((s) => ({
      ...s,
      chapters: regular.filter((c) => c.projectTemplateChapter.section === s.key)
    }));
    // A regular chapter without a section means the template marks it hidden (or exec) —
    // e.g. an old project where the chapter is still visible. Group it under Hidden Chapters.
    // If the template has no sections at all (not migrated yet), keep the flat list instead.
    const unsectioned = templateHasSections ? [] : regular.filter((c) => !c.projectTemplateChapter.section);
    const hiddenChapters = [
      ...projectChapters.filter((c) => !specialIds.has(c.id) && c.isHidden),
      ...(templateHasSections ? regular.filter((c) => !c.projectTemplateChapter.section) : [])
    ];

    return { execChapter, memoChapter, appendices, hiddenChapters, sections, unsectioned };
  }, [projectChapters]);

  const activeChapterId = activeChapter?.id;

  useEffect(() => {
    if (!activeChapterId) return;
    const owningSection = chapters.sections.find((s) => s.chapters.some((c) => c.id === activeChapterId));
    if (owningSection) {
      setExpandedSections((prev) => (prev.has(owningSection.key) ? prev : new Set(prev).add(owningSection.key)));
    }
    if (chapters.appendices.some((c) => c.id === activeChapterId)) {
      setExpandedSections((prev) => (prev.has("appendices") ? prev : new Set(prev).add("appendices")));
    }
    if (chapters.hiddenChapters.some((c) => c.id === activeChapterId)) {
      setExpandedSections((prev) => (prev.has("hidden") ? prev : new Set(prev).add("hidden")));
    }
  }, [activeChapterId, chapters]);

  const toggleSection = (key: string) => {
    if (collapsed) {
      toggleChapterSidebar(true);
      setExpandedSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
      return;
    }
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const guardCaseFrameLeave = (leave: () => void) => {
    if (viewMode === "projectFrame" && isCaseFrameDirty) {
      setCaseFramePendingLeave(leave);
      return;
    }
    leave();
  };

  const openChapter = (chapterId: number) => {
    if (isMobile) toggleChapterSidebar(false);
    if (viewMode === "chapter" && activeChapter?.id === chapterId) return;
    guardCaseFrameLeave(() => {
      setActiveChapter(chapterId.toString());
      setViewMode("chapter");
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "document");
        params.set("chapterId", chapterId.toString());
        params.delete("chapterSubsection");
        params.delete("view");
        return params;
      });
    });
  };

  const openHome = () => {
    if (isMobile) toggleChapterSidebar(false);
    if (viewMode === "dashboard" || viewMode === "projectOverview") return;
    guardCaseFrameLeave(() => {
      closeHandler();
      setViewMode("projectOverview");
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "overview");
        params.delete("chapterId");
        params.delete("chapterSubsection");
        params.delete("view");
        return params;
      });
    });
  };

  const openFrameView = (view: "projectFrame" | "projectConfig" | "vault" | "exports" | "valueChain") => {
    if (isMobile) toggleChapterSidebar(false);
    const navigateToView = () => {
      closeHandler();
      setViewMode(view);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", view);
        params.delete("chapterId");
        params.delete("chapterSubsection");
        return params;
      });
    };
    if (view === "projectFrame") navigateToView();
    else guardCaseFrameLeave(navigateToView);
  };

  const isHomeActive = viewMode === "dashboard" || viewMode === "projectOverview";

  // Per Figma the value chain is a page of Market Analysis, not a Context entry.
  const VALUE_CHAIN_SECTION = "market-analysis";
  const hasValueChain = useFeatureFlag("value-chain", selectedProject?.organizationId);

  const renderChapterItem = (chapter: TChapter, submenu: boolean) => (
    <SidebarButton
      key={chapter.id}
      label={chapter.projectTemplateChapter.name}
      active={viewMode === "chapter" && activeChapter?.id === chapter.id}
      collapsed={collapsed && !submenu}
      onClick={() => openChapter(chapter.id)}
      right={<ChapterIndicator chapter={chapter} projectId={projectId} />}
    />
  );

  const renderSectionGroup = (key: string, label: string, icon: React.ReactNode, sectionChapters: TChapter[]) => {
    if (!sectionChapters.length) return null;
    const isExpanded = expandedSections.has(key);
    // Collapsed hides the submenu, so the section itself carries the active chapter's state.
    const holdsActiveChapter =
      (viewMode === "chapter" && sectionChapters.some((chapter) => chapter.id === activeChapter?.id)) ||
      (key === VALUE_CHAIN_SECTION && viewMode === "valueChain");
    return (
      <div key={key} className="w-full">
        <SidebarButton
          icon={icon}
          label={label}
          active={collapsed && holdsActiveChapter}
          collapsed={collapsed}
          caret={isExpanded ? "up" : "down"}
          onClick={() => toggleSection(key)}
        />
        <SidebarSubMenu expanded={isExpanded && !collapsed}>
          {sectionChapters.map((chapter) => renderChapterItem(chapter, true))}
          {key === VALUE_CHAIN_SECTION && !creation && hasValueChain && (
            <SidebarButton
              label={t("sidebar.valueChain")}
              active={viewMode === "valueChain"}
              onClick={() => openFrameView("valueChain")}
            />
          )}
        </SidebarSubMenu>
      </div>
    );
  };

  const onClickAway = () => {
    if (!creation && isMobile && isOpenChapterSidebar) toggleChapterSidebar(false);
  };

  return (
    <ClickAwayListener insideSelectors={["[data-chapter-sidebar-toggle]"]} onClickAway={onClickAway}>
      <div
        data-testid={creation ? "create-project-sidebar" : "sidebar-chapters-root"}
        className={cn(
          "flex flex-col bg-white pt-2 transition-all duration-300",
          collapsed ? "w-12" : "w-60",
          creation
            ? "shrink-0 h-full"
            : cn(
                "absolute top-16 -left-64 z-10 h-[calc(100svh-129px)]",
                "hd:static hd:h-auto hd:shrink-0",
                isMobile && isOpenChapterSidebar && "left-2 z-[100] border-r border-innoveraZinc-100 shadow",
                !isMobile && "static h-auto shrink-0"
              )
        )}
      >
        <div
          className={cn(
            "flex-1 flex flex-col w-full px-2 overflow-y-auto overflow-x-hidden custom-thin-scrollbar pb-2",
            collapsed ? "gap-0" : "gap-4"
          )}
        >
          <div className="flex flex-col gap-px w-full">
            <SidebarButton
              icon={<SidebarHomeIcon />}
              label={t("sidebar.home")}
              active={!creation && isHomeActive}
              collapsed={collapsed}
              disabled={!!creation}
              right={creation ? <LockBadge /> : undefined}
              onClick={creation ? undefined : openHome}
              testId="sidebar-home"
            />
            {(creation || chapters.execChapter) && (
              <SidebarButton
                icon={<SidebarExecSummaryIcon />}
                label={t("sidebar.executiveSummary")}
                active={!creation && viewMode === "chapter" && activeChapter?.id === chapters.execChapter?.id}
                collapsed={collapsed}
                disabled={!!creation}
                onClick={creation ? undefined : () => openChapter(chapters.execChapter!.id)}
                right={
                  creation ? <LockBadge /> : <ChapterIndicator chapter={chapters.execChapter!} projectId={projectId} />
                }
              />
            )}
            {(creation || chapters.memoChapter) && (
              <SidebarButton
                icon={<SidebarMemoIcon />}
                label={t("sidebar.innoveraMemo")}
                active={!creation && viewMode === "chapter" && activeChapter?.id === chapters.memoChapter?.id}
                collapsed={collapsed}
                disabled={!!creation}
                onClick={creation ? undefined : () => openChapter(chapters.memoChapter!.id)}
                right={
                  creation ? <LockBadge /> : <ChapterIndicator chapter={chapters.memoChapter!} projectId={projectId} />
                }
              />
            )}
          </div>

          {creation ? (
            <div className="flex flex-col gap-px w-full">
              <SidebarGroupLabel label={t("sidebar.deepDives")} collapsed={collapsed} />
              {DEEP_DIVE_SECTIONS.map(({ key, labelKey, Icon }) => (
                <SidebarButton
                  key={key}
                  icon={<Icon />}
                  label={t(labelKey)}
                  collapsed={collapsed}
                  disabled
                  right={<LockBadge />}
                />
              ))}
            </div>
          ) : (
            (chapters.sections.some((s) => s.chapters.length > 0) || chapters.unsectioned.length > 0) && (
              <div className="flex flex-col gap-px w-full">
                <SidebarGroupLabel label={t("sidebar.deepDives")} collapsed={collapsed} />
                {chapters.sections.map((section) =>
                  renderSectionGroup(section.key, t(section.labelKey), <section.Icon />, section.chapters)
                )}
                {chapters.unsectioned.map((chapter) => renderChapterItem(chapter, false))}
                {chapters.appendices.length > 0 &&
                  renderSectionGroup(
                    "appendices",
                    t("sidebar.appendices"),
                    <SparkleIcon className="size-4 shrink-0" weight="regular" />,
                    chapters.appendices
                  )}
                {isLabs &&
                  chapters.hiddenChapters.length > 0 &&
                  renderSectionGroup(
                    "hidden",
                    t("sidebar.hiddenChapters"),
                    <SparkleIcon className="size-4 shrink-0" weight="regular" />,
                    chapters.hiddenChapters
                  )}
              </div>
            )
          )}

          <div className="flex flex-col gap-px w-full">
            <SidebarGroupLabel label={t("sidebar.context")} collapsed={collapsed} />
            <div className="w-full">
              <SidebarButton
                icon={<SidebarFrameIcon />}
                label={t("sidebar.frame")}
                active={collapsed && (viewMode === "projectConfig" || viewMode === "projectFrame")}
                collapsed={collapsed}
                caret={expandedSections.has("frame") ? "up" : "down"}
                onClick={() => toggleSection("frame")}
              />
              <SidebarSubMenu expanded={expandedSections.has("frame") && !collapsed}>
                {creation ? (
                  <>
                    <SidebarButton
                      label={t("sidebar.projectSetup")}
                      active={creation.view === "projectSetup"}
                      onClick={() => creation.onViewChange("projectSetup")}
                    />
                    <SidebarButton
                      label={t("sidebar.caseFrame")}
                      active={creation.view === "caseFrame"}
                      disabled={creation.caseFrameLocked}
                      right={creation.caseFrameLocked ? <LockBadge /> : undefined}
                      onClick={creation.caseFrameLocked ? undefined : () => creation.onViewChange("caseFrame")}
                    />
                  </>
                ) : (
                  <>
                    <SidebarButton
                      label={t("sidebar.projectSetup")}
                      active={viewMode === "projectConfig"}
                      onClick={() => openFrameView("projectConfig")}
                    />
                    <SidebarButton
                      label={t("sidebar.caseFrame")}
                      active={viewMode === "projectFrame"}
                      onClick={() => openFrameView("projectFrame")}
                    />
                  </>
                )}
              </SidebarSubMenu>
            </div>
            <SidebarButton
              icon={<SidebarVaultIcon />}
              label={t("sidebar.vault")}
              active={!creation && viewMode === "vault"}
              collapsed={collapsed}
              disabled={!!creation}
              right={creation ? <LockBadge /> : undefined}
              onClick={creation ? undefined : () => openFrameView("vault")}
            />
          </div>

          <div className="flex flex-col gap-px w-full">
            <SidebarGroupLabel label={t("sidebar.artifacts")} collapsed={collapsed} />
            <SidebarButton
              icon={<SidebarExportIcon />}
              label={t("sidebar.exports")}
              active={!creation && viewMode === "exports"}
              collapsed={collapsed}
              disabled={!!creation}
              right={creation ? <LockBadge /> : undefined}
              onClick={creation ? undefined : () => openFrameView("exports")}
            />
          </div>
        </div>

        <SidebarCollapseFooter
          collapsed={collapsed}
          onToggle={() => toggleChapterSidebar(!isOpenChapterSidebar)}
          testId="sidebar-toggle-button"
        />
      </div>
    </ClickAwayListener>
  );
}
